// Reverse of booking-v2-payload.mapper: a loaded BcmBooking → wizard seed state
// for edit mode. Pure (no facade/HTTP). Each loaded child keeps its backend id
// so the commit can diff/remove it.

import { v4 as uuidv4 } from 'uuid';

import { BcmBooking, IBcmBookingKeyAssignment } from '@shared/models/bcm-booking';
import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { InvoicePosition } from '@shared/models/invoice-position';
import { ProductSubscription, ProductSubscriptionType } from '@shared/models/product-subscription';
import { ElectricMeterAssignment } from '@shared/models/electric-meter-assignment';
import { tryParseDate } from '@shared/functions/try-parse-date';

import {
    BookingV2BerthOccupancy,
    BookingV2BoatOption,
    BookingV2BookingDocument,
    BookingV2ElectricMeterAssignment,
    BookingV2KeyAssignment,
    BookingV2PersonOption,
    BookingV2PricingRelationIds,
    BookingV2RelationLineOverrides,
    BookingV2RelationOption,
    BookingV2Service,
    BookingV2Traveler,
    BookingV2WinterStorageOption,
} from '../dtos/booking-v2.dtos';
import {
    toBerthOption,
    toBoatOption,
    toCompanyOption,
    toPersonOption,
    toRelationOption,
} from '../utils/booking-v2-option.mappers';
import { recoverWinterStorageOption } from '../utils/booking-v2-winter-storage';
import { recoverRelationLines } from '../utils/booking-v2-relation-line-recovery';
import { recoverServiceAutoName } from '../utils/booking-v2-service-naming';
import { needsPricingRelation } from '../utils/booking-v2-pricing-relation';
import { BookingV2DateRange, BookingV2Type } from './booking-v2.facade';
import { occupancyMatchesBookingDateRange } from '../utils/booking-v2-berth-occupancy-dates.util';

export interface BookingV2LoadResult {
    bookingId: number;
    // Preserved on save (the wizard has no status UI).
    statusId: number;
    bookingType: BookingV2Type;
    dateRange: BookingV2DateRange;
    // One entry per berthed assignment, each with its own window (6609).
    occupancies: BookingV2BerthOccupancy[];
    // Id of an existing berth-less assignment (boat held with no berth), so edit
    // mode patches it instead of duplicating it.
    berthlessAssignmentId: number | null;
    boat: BookingV2BoatOption | null;
    person: BookingV2PersonOption | null;
    relation: BookingV2RelationOption | null;
    notes: string;
    // Reservation mode: confirm-by deadline + free-text note.
    reservedUntil: Date | null;
    reservationText: string;
    travelers: BookingV2Traveler[];
    services: BookingV2Service[];
    // Ids of the relation's one-off positions (fromTenantRelation) — NOT editable
    // services, but tracked so edit can delete+reinsert them without duplicating
    // (they are regenerated from the relation on save). Paid ones are excluded.
    relationPositionIds: number[];
    // Hand edits and picked pricing relations of those positions, which the
    // regeneration would otherwise drop (#7044).
    relationLineOverrides: BookingV2RelationLineOverrides;
    relationPricingRelationIds: BookingV2PricingRelationIds;
    // Open positions the scheduler generated for the wizard's Abos, by Abo id — not lines
    // of their own; removed together with their Abo (#7044).
    subscriptionPositionIds: Record<number, number[]>;
    // Ids of the key-deposit (Pfand) positions — likewise regenerated from the
    // keys on save (delete+reinsert), so kept out of the editable services.
    depositPositionIds: number[];
    electricMeters: BookingV2ElectricMeterAssignment[];
    keys: BookingV2KeyAssignment[];
    documents: BookingV2BookingDocument[];
    // Lagerplatz recovered from the saved dynamicPrice provenance (it is not a
    // booking field). Restoring it before the live re-eval reproduces the stored
    // price instead of silently degrading to the non-dynamic fallback. `null` when
    // no rule branched on a Lagerplatz.
    winterStorage: BookingV2WinterStorageOption | null;
    selectedOrderId: number | null;
}

export function mapBookingToWizardState(booking: BcmBooking): BookingV2LoadResult {

    const bookingType: BookingV2Type =
        booking.general?.bookingType === 'reservation' ? 'reservation' : 'booking';

    // `booking.to` is an Invalid Date for an open-ended booking (the model maps
    // it via Math.max over berth-assignment `to`s, which yields NaN when one is
    // open). Normalise invalid dates to null so `openEnd` is derived correctly
    // and date formatting never throws.
    const from = isValidDate(booking.from) ? booking.from : null;
    const to = isValidDate(booking.to) ? booking.to : null;

    const dateRange: BookingV2DateRange = {
        from,
        to,
        openEnd: !to,
    };

    // Each berthed assignment → one occupancy carrying its own window. An
    // assignment with no `to` is an open-ended occupancy.
    const occupancies: BookingV2BerthOccupancy[] = (booking.berthAssignments || [])
        .filter(a => !!a.berth)
        .map(a => {
            const occFrom = tryParseDate(a.from) ?? null;
            const occTo = tryParseDate(a.to) ?? null;
            const occOpenEnd = !occTo;
            return {
                uuid: uuidv4(),
                berth: toBerthOption(a.berth),
                from: occFrom,
                to: occTo,
                openEnd: occOpenEnd,
                // Only pin windows that differ from the booking range; otherwise the
                // date step can update occupancies on edit (#6989).
                overridden: !occupancyMatchesBookingDateRange(occFrom, occTo, occOpenEnd, dateRange),
                assignmentId: a.id,
            };
        });

    const berthlessAssignmentId = (booking.berthAssignments || [])
        .find(a => !a.berth)?.id ?? null;

    const boat: BookingV2BoatOption | null = booking.boat
        ? { ...toBoatOption(booking.boat), origin: 'existing' }
        : null;

    let person: BookingV2PersonOption | null = booking.company
        ? toCompanyOption(booking.company)
        : booking.person
            ? toPersonOption(booking.person)
            : null;

    // Bookings created via the berth/boat workflow carry no direct person on the
    // booking row — the de-facto booking person is the boat owner. Fall back to it
    // (mirrors the create-flow boat-owner default) so such bookings can be
    // edited/saved and their positions get a recipient.
    if (!person && boat && boat.ownerId != null && boat.ownerKind != null) {
        person = {
            id: boat.ownerId,
            kind: boat.ownerKind,
            displayName: boat.ownerLabel ?? '',
            contactPerson: null,
            address: null,
            phone: null,
            mail: null,
            origin: 'existing',
        };
    }

    const tra = booking.tenantRelationAssignment;
    const relation: BookingV2RelationOption | null = tra?.tenantRelation
        ? { ...toRelationOption(tra.tenantRelation), assignmentId: tra.id }
        : null;

    const travelers: BookingV2Traveler[] = (booking.travelers || []).map(t => ({
        firstName: t.firstName,
        lastName: t.lastName,
    }));

    // Deposit (Pfand) positions are plain positions whose product is one of the
    // booking's key-deposit products — split off so edit regenerates them from the
    // keys instead of surfacing them as free, editable services. HEURISTIC: there
    // is no backend discriminator, so a plain service line that happens to use a
    // deposit product would be misclassified as a deposit (re-billed via the keys).
    // Acceptable — selling a key-deposit product as a normal booking service is not
    // a real flow; a position↔key FK would make this exact.
    const depositProductIds = new Set(
        (booking.keyAssignments || [])
            .map(k => k.key?.bcm_products_id)
            .filter((id): id is number => id != null),
    );
    const isDepositPosition = (p: InvoicePosition): boolean =>
        isPlainServicePosition(p) && p.product?.id != null && depositProductIds.has(p.product.id);

    // Berth id per saved berth assignment — lets a position linked to a berth keep
    // that link when the booking is saved again (#6734). Without it the backend
    // cannot infer the assignment for a booking with several berths and would null
    // the FK the Bierdeckel/Export relies on.
    const berthIdByAssignmentId = new Map<number, number>(
        (booking.berthAssignments || [])
            .filter(a => !!a.berth && a.id != null)
            .map(a => [a.id, a.berth.id] as [number, number]),
    );

    const relationId = relation?.id ?? null;

    // The first position of an Abo is written right on save; the Abo itself is the editable
    // line, so listing its positions too showed every Abo twice. Only Abos of the booking's
    // own person/company are lines here — positions of other Abos stay visible. (#7044)
    const editableSubscriptions = (booking.subscriptions || []).filter(isEditableSubscription);
    const editableSubscriptionIds = new Set(editableSubscriptions.map(s => s.id));
    const subscriptionIdOf = (p: InvoicePosition): number | null =>
        (booking.company ? p.bcm_company_has_products_id : p.bcm_person_has_products_id) ?? null;
    const isWizardSubscriptionPosition = (p: InvoicePosition): boolean =>
        editableSubscriptionIds.has(subscriptionIdOf(p));

    const services: BookingV2Service[] = [
        ...(booking.positions || [])
            .filter(p => isPlainServicePosition(p) && !isDepositPosition(p) && !isWizardSubscriptionPosition(p))
            .map(p => toServiceFromPosition(p, berthIdByAssignmentId, relationId)),
        ...editableSubscriptions.map(s => toServiceFromSubscription(s, relationId)),
    ];

    const subscriptionPositionIds: Record<number, number[]> = {};
    for (const p of booking.positions || []) {
        if (isWizardSubscriptionPosition(p) && !p.isPaid) {
            const subscriptionId = subscriptionIdOf(p) as number;
            (subscriptionPositionIds[subscriptionId] ??= []).push(p.id);
        }
    }

    const relationPositions = (booking.positions || []).filter(isRelationPosition);
    const relationPositionIds = relationPositions.map(p => p.id);
    const recoveredRelationLines = recoverRelationLines(relationPositions, relationId);

    // Paid deposit positions are left out so the edit diff never deletes a settled one.
    const depositPositionIds = (booking.positions || [])
        .filter(p => isDepositPosition(p) && !p.isPaid)
        .map(p => p.id);

    const electricMeters = (booking.electricMeterAssignments || []).map(toMeterAssignment);

    const keys = (booking.keyAssignments || []).map(toKeyAssignment);

    const documents = (booking.documents || []).filter(d => !!d.file).map(toBookingDocument);

    const winterStorage = recoverWinterStorage(booking);

    return {
        bookingId: booking.id,
        statusId: Number(booking.general?.status?.id) || 1,
        bookingType,
        dateRange,
        occupancies,
        berthlessAssignmentId,
        boat,
        person,
        relation,
        notes: booking.general?.note ?? '',
        reservedUntil: tryParseDate(booking.general?.reservedUntil) ?? null,
        reservationText: booking.general?.reservationText ?? '',
        travelers,
        services,
        relationPositionIds,
        relationLineOverrides: recoveredRelationLines.overrides,
        relationPricingRelationIds: recoveredRelationLines.pricingRelationIds,
        subscriptionPositionIds,
        depositPositionIds,
        electricMeters,
        keys,
        documents,
        winterStorage,
        selectedOrderId: booking.bcm_orders_id ?? null,
    };
}

// The Lagerplatz is not a booking field. Primary source: the relation assignment
// row, where it is persisted as a pricing input (mirrors the membership route) — the
// reliable source for an open-ended relation that emits no one-off position. Fallback:
// the dynamicPrice provenance of a position/subscription (covers a free dynamic
// service priced by a Lagerplatz, which has no relation assignment).
function recoverWinterStorage(booking: BcmBooking): BookingV2WinterStorageOption | null {
    return recoverWinterStorageOption(
        booking.tenantRelationAssignment?.winterStorage,
        [
            ...(booking.positions || []).map(p => p.dynamicPrice?.winterStorage),
            ...(booking.subscriptions || []).map(s => s.dynamicPrice?.winterStorage),
        ],
    );
}

// Only plain one-off positions become editable services on edit. NB: the backend
// stamps bcm_berth_has_assignments_id onto EVERY Bierdeckel position
// (single-assignment fallback in resolveBerthAssignmentIdFromMap, fix 6505) for
// the CSV/Excel export, so that FK is NOT a discriminator. The real
// "managed-elsewhere" signals are meter / tenant-relation / subscription — those
// are reconstructed from the relation/subscriptions graph, not edited here as free
// services. A `dynamicPrice` is NOT a discriminator: a free dynamic-priced service
// (rule-based product added in the services tab) carries one and must round-trip;
// relation dynamic lines are already excluded by their fromTenantRelation flag.
// (The Beleg transfer is deliberately looser — see selectBelegPositions: a
// Beziehung line IS billable on a new Beleg.)
function isPlainServicePosition(p: InvoicePosition): boolean {
    return !p.bcm_electric_meter_has_boats_id
        && !p.fromTenantRelation
        && !p.tenantRelationAssignment
        && !p.subscription;
}

// A relation's one-off Leistung position (regenerated from the relation on edit).
// Paid ones are left out so the edit diff never deletes a settled position.
function isRelationPosition(p: InvoicePosition): boolean {
    return (!!p.fromTenantRelation || !!p.tenantRelationAssignment)
        && !p.bcm_electric_meter_has_boats_id
        && !p.isPaid;
}

function toServiceFromPosition(
    p: InvoicePosition,
    berthIdByAssignmentId: Map<number, number>,
    relationId: number | null,
): BookingV2Service {
    // The berth this position is linked to, if any. `managed: false` — the line is
    // an ordinary editable service from here on; the berth is remembered only so the
    // re-save keeps its bcm_berth_has_assignments_id, never to re-seed or requantify it.
    const berthId = p.bcm_berth_has_assignments_id != null
        ? berthIdByAssignmentId.get(p.bcm_berth_has_assignments_id)
        : undefined;

    return {
        id: uuidv4(),
        positionId: p.id,
        ...(berthId != null
            ? {
                berthOrigin: {
                    berthId,
                    managed: false,
                    ignoreFreeOfChargePosition: false,
                    seededSignature: '',
                },
            }
            : {}),
        // Already settled on an invoice/receipt → read-only, never re-sent.
        isProtected: p.isPaid,
        productId: p.product?.id ?? null,
        name: p.title,
        autoName: recoverServiceAutoName(p.title, p.dynamicPrice?.ruleName, p.product?.name),
        category: p.product?.category?.name ?? null,
        quantity: p.quantity,
        unitId: p.unit?.id ?? null,
        unitLabelKey: p.unit?.translationKey ?? null,
        price: p.price,
        taxRateId: p.taxRate?.id ?? null,
        vatValue: p.taxRate?.value ?? p.taxRateValue ?? 0,
        discount: p.discountPercentage ?? undefined,
        costCenterId: p.costCenter?.id ?? null,
        serviceStart: p.vestingPeriodFromDate ?? null,
        serviceEnd: p.vestingPeriodUntilDate ?? null,
        serviceEndOpen: !p.vestingPeriodUntilDate,
        isSubscription: false,
        // Edit round-trip for a free dynamic-priced service: keep the rule flag,
        // provenance and (best-effort) requirements so the line stays locked +
        // re-priceable. `requiredEntities` may be empty if the booking graph omits
        // priceRuleMap — the line still degrades to a static (but preserved) price.
        hasDynamicPrice: !!p.dynamicPrice || !!p.product?.hasDynamicPrice,
        dynamicRuleName: p.dynamicPrice?.ruleName ?? null,
        dynamicPrice: (p.dynamicPrice as unknown as Partial<BcmDynamicPriceDto>) ?? null,
        requiredEntities: p.product?.priceRuleMap?.getRequirements?.() ?? [],
        ...pricingRelationOf(p, relationId),
    };
}

// The rule's relation criteria plus the Beziehung picked for them, read back from the
// saved provenance — only kept while the booking's own relation does not satisfy the rule.
function pricingRelationOf(
    row: InvoicePosition | ProductSubscription,
    relationId: number | null,
): Pick<BookingV2Service, 'tenantRelationCriteriaIds' | 'pricingRelationId'> {
    const criteria = row.product?.priceRuleMap?.getTenantRelationCriteriaIds?.() ?? [];
    const hasDynamicPrice = !!row.dynamicPrice || !!row.product?.hasDynamicPrice;
    const picked = row.dynamicPrice?.tenantRelation?.id ?? null;
    return {
        tenantRelationCriteriaIds: criteria,
        pricingRelationId: needsPricingRelation(hasDynamicPrice, criteria, relationId) && picked != null && criteria.includes(picked)
            ? picked
            : null,
    };
}

// Real person/company subscriptions only — the synthetic tenant-relation rows
// (id <= 0) are derived from the relation and must not be edited here.
function isEditableSubscription(s: ProductSubscription): boolean {
    return s.id > 0 && s.type !== ProductSubscriptionType.TenantRelation;
}

function toServiceFromSubscription(s: ProductSubscription, relationId: number | null): BookingV2Service {
    const name = s.customTitle || s.dynamicPrice?.ruleName || s.product?.name || '';
    const ruleName = s.dynamicPrice?.ruleName;
    return {
        id: uuidv4(),
        subscriptionId: s.id,
        productId: s.product?.id ?? null,
        name,
        // A rule-priced Abo stores the shown name as customTitle; it is the user's own only when
        // it is neither the rule's nor the product's name (#7044).
        autoName: s.customTitle ? recoverServiceAutoName(s.customTitle, ruleName, s.product?.name) : name,
        category: s.product?.category?.name ?? null,
        quantity: s.quantity,
        unitId: s.product?.unit?.id ?? null,
        unitLabelKey: s.product?.unit?.translationKey ?? null,
        price: s.customPrice ?? s.product?.price ?? 0,
        taxRateId: s.product?.taxRate?.id ?? null,
        vatValue: s.product?.taxRate?.value ?? 0,
        costCenterId: s.costCenter?.id ?? null,
        serviceStart: s.fromDate ?? null,
        serviceEnd: s.toDate ?? null,
        serviceEndOpen: !s.toDate,
        isSubscription: true,
        // Same edit round-trip as the position path (best-effort provenance).
        hasDynamicPrice: !!s.dynamicPrice || !!s.product?.hasDynamicPrice,
        dynamicRuleName: s.dynamicPrice?.ruleName ?? null,
        dynamicPrice: (s.dynamicPrice as unknown as Partial<BcmDynamicPriceDto>) ?? null,
        requiredEntities: s.product?.priceRuleMap?.getRequirements?.() ?? [],
        ...pricingRelationOf(s, relationId),
        subscriptionConfig: {
            payableOptionId: s.payableOption?.id ?? null,
            payableOptionLabelKey: s.payableOption?.translationKey ?? null,
            fromDate: s.fromDate ?? null,
            toDate: s.toDate ?? null,
            vestingPeriodFrom: s.vestingPeriodFrom ?? null,
            vestingPeriodUntil: s.vestingPeriodUntil ?? null,
            lastDayOfMonth: s.lastDayOfMonth ?? false,
        },
    };
}

// Edit mode: an assignment already loaded from the booking — keeps its own berth.
function toMeterAssignment(a: ElectricMeterAssignment): BookingV2ElectricMeterAssignment {
    const berth = a.berthAssignment?.berth as any;
    return buildMeterAssignment(a, {
        berthId: berth?.id ?? null,
        berthLabel: berth ? (berth.pier?.handle ? `${berth.pier.handle} · ${berth.handle}` : berth.handle) : null,
    });
}

// Create mode: a boat's pre-existing OPEN standalone assignment proposed for
// take-over. Flagged `reparent` so the commit re-parents it (PATCH) instead of
// creating; berth is left null so it attaches to the booking's primary berth.
export function toProposedMeterAssignment(a: ElectricMeterAssignment): BookingV2ElectricMeterAssignment {
    return buildMeterAssignment(a, { reparent: true, berthId: null, berthLabel: null });
}

function buildMeterAssignment(
    a: ElectricMeterAssignment,
    extra: Partial<BookingV2ElectricMeterAssignment>,
): BookingV2ElectricMeterAssignment {
    // Display-only fields read defensively; a kept/proposed meter is never re-sent
    // verbatim — only assignmentId + readingId matter (for removal / re-parent).
    const meter = a.electricMeter as any;
    const productType = a.productType as any;
    return {
        uuid: uuidv4(),
        assignmentId: a.id,
        readingId: a.reading?.id,
        berthId: null,
        berthLabel: null,
        cabinetId: meter?.cabinet?.id ?? 0,
        cabinetHandle: meter?.cabinet?.handle ?? '',
        meterId: meter?.id ?? 0,
        meterHandle: meter?.handle ?? '',
        meterFullName: meter?.fullName ?? meter?.handle ?? '',
        from: a.reading?.startDate ?? a.from ?? null,
        meterReadingStart: a.meterReadingStart ?? 0,
        // The model derives `meterReadingEnd` via Math.max over readings, which
        // yields 0/NaN for a still-open connection — only trust it once the
        // connection is actually closed (`to` set), otherwise treat it as open.
        meterReadingEnd: a.to != null && Number.isFinite(a.meterReadingEnd) ? a.meterReadingEnd : null,
        to: a.to != null ? (a.reading?.endDate ?? a.to ?? null) : null,
        productTypeId: productType?.id ?? 0,
        productTypeKey: productType?.key ?? '',
        productTypeLabel: productType?.description ?? '',
        productId: a.product?.id ?? null,
        productName: a.product?.name ?? null,
        quantity: a.quantity ?? null,
        // Display-only (the gross shown in the summary); may be absent from the
        // edit-load graph, in which case the flat-rate row shows no amount.
        productPrice: a.product?.price ?? null,
        productVatValue: (a.product as any)?.taxRate?.value ?? null,
        // Existing fee already lives as its own invoice position — not re-derived.
        additionalFee: null,
        provider: meter?.cabinet?.provider
            ? { switchableViaWeb: !!meter.cabinet.provider.switchableViaWeb }
            : null,
        hasDualUse: !!meter?.hasDualUse,
        ...extra,
    };
}

function toKeyAssignment(k: IBcmBookingKeyAssignment): BookingV2KeyAssignment {
    const personId = k.bcm_persons_id ?? k.person?.id ?? null;
    const boatId = k.bcm_boats_id ?? k.boat?.id ?? null;
    // tax/unit aren't declared on the edit-load key.product type — read via any.
    const depositProduct = k.key?.product as any;
    const personLabel = k.person
        ? (k.person.fullName || `${k.person.firstName ?? ''} ${k.person.lastName ?? ''}`.trim() || null)
        : null;
    return {
        uuid: uuidv4(),
        assignmentId: k.id,
        // Loaded lendings always reference an existing catalog key.
        origin: 'catalog',
        catalogKeyId: k.key?.id ?? null,
        keyName: k.key?.keyName ?? '',
        keyTypeLabel: k.key?.keyType ?? null,
        keyType: null,
        keyIdentifier: k.key?.keyId ?? null,
        uniqueness: k.key?.isUnique ? 'unique' : 'duplicated',
        personId,
        personKind: personId != null ? 'person' : null,
        personLabel,
        boatId,
        boatLabel: k.boat?.name ?? null,
        keyNumber: k.keyNumber ?? null,
        from: tryParseDate(k.fromDate) ?? null,
        to: tryParseDate(k.toDate) ?? null,
        note: k.note ?? null,
        depositProductId: k.key?.bcm_products_id ?? null,
        depositProductName: k.key?.product?.name ?? null,
        depositAmount: k.key?.product?.price ?? null,
        // tax/unit aren't on the edit-load key.product type and may be absent at
        // runtime; read defensively, falling back to null (the Pfand position is
        // still billable, just without resolved VAT until re-picked).
        depositTaxRateId: depositProduct?.taxRate?.id ?? null,
        depositUnitId: depositProduct?.unit?.id ?? null,
        depositVatValue: depositProduct?.taxRate?.value ?? null,
        keyFeePayedOn: tryParseDate(k.keyFeePayedOn) ?? null,
        keyFeePayedBackOn: tryParseDate(k.keyFeePayedBackOn) ?? null,
    };
}

function isValidDate(d: Date | null | undefined): d is Date {
    return d instanceof Date && !isNaN(d.getTime());
}

function toBookingDocument(d: BcmBooking['documents'][number]): BookingV2BookingDocument {
    return {
        id: uuidv4(),
        documentId: d.id,
        name: d.title ?? d.file?.fileName ?? '',
        categoryId: d.documentCategory?.id ?? null,
        categoryName: d.documentCategory?.name ?? null,
        bcmFilesId: d.file?.id ?? 0,
        fileName: d.file?.fileName ?? d.file?.name ?? '',
        mimeType: d.file?.mimeType,
        uploadDate: d.insertedOn ?? new Date(),
    };
}
